/**
 * Iranian IBAN (شبا) helpers.
 *
 * Format: `IR` + 2 check digits + 22-digit BBAN (26 characters). The first
 * three BBAN digits (IBAN positions 5 to 7, e.g. "017" in "IR06 0170 ...")
 * identify the bank. Validation is ISO 13616 / ISO 7064 mod-97-10.
 */

import { toAsciiDigits } from './digits';

const SHEBA_RE = /^IR\d{24}$/;

/**
 * Normalize user input: Persian/Arabic digits to ASCII, strip whitespace,
 * dashes, dots and bidi marks, uppercase, and prefix "IR" when exactly 24
 * digits were given. Does not validate.
 */
export function normalizeSheba(input: string): string {
  let s = toAsciiDigits(input);
  s = s.replace(/[\s\-‐-―−._​-‏‪-‮⁦-⁩﻿]/g, '');
  s = s.toUpperCase();
  if (/^\d{24}$/.test(s)) s = `IR${s}`;
  return s;
}

/** ISO 7064 mod 97 of a string of ASCII digits and A-Z letters (A=10 ... Z=35). */
function mod97(value: string): number {
  let remainder = 0;
  for (const ch of value) {
    const code = ch.charCodeAt(0);
    const digits = code >= 65 ? String(code - 55) : ch;
    for (const d of digits) remainder = (remainder * 10 + (d.charCodeAt(0) - 48)) % 97;
  }
  return remainder;
}

/** True when the input (after `normalizeSheba`) is `IR` + 24 digits with valid mod-97 check digits. */
export function isValidSheba(input: string): boolean {
  const s = normalizeSheba(input);
  if (!SHEBA_RE.test(s)) return false;
  return mod97(s.slice(4) + s.slice(0, 4)) === 1;
}

/**
 * Compute the two IBAN check digits for an Iranian 22-digit BBAN.
 * `IR${computeShebaCheckDigits(bban)}${bban}` is then a valid Sheba.
 */
export function computeShebaCheckDigits(bban: string): string {
  if (!/^\d{22}$/.test(bban)) throw new RangeError('BBAN must be exactly 22 ASCII digits');
  const check = 98 - mod97(`${bban}IR00`);
  return String(check).padStart(2, '0');
}

export interface BankInfo {
  /** 3-digit bank code, e.g. "017". */
  code: string;
  /** Persian bank name. */
  nameFa: string;
  /** True when the bank has been merged into another bank (account may still work). */
  merged?: true;
}

/** Bank / credit-institution codes as they appear in IBAN positions 5 to 7. */
export const IRAN_BANKS: Readonly<Record<string, BankInfo>> = Object.freeze(
  Object.fromEntries(
    (
      [
        ['010', 'بانک مرکزی'],
        ['011', 'بانک صنعت و معدن'],
        ['012', 'بانک ملت'],
        ['013', 'بانک رفاه کارگران'],
        ['014', 'بانک مسکن'],
        ['015', 'بانک سپه'],
        ['016', 'بانک کشاورزی'],
        ['017', 'بانک ملی ایران'],
        ['018', 'بانک تجارت'],
        ['019', 'بانک صادرات ایران'],
        ['020', 'بانک توسعهٔ صادرات'],
        ['021', 'پست‌بانک'],
        ['022', 'بانک توسعهٔ تعاون'],
        ['052', 'بانک قوامین', true],
        ['053', 'بانک کارآفرین'],
        ['054', 'بانک پارسیان'],
        ['055', 'بانک اقتصاد نوین'],
        ['056', 'بانک سامان'],
        ['057', 'بانک پاسارگاد'],
        ['058', 'بانک سرمایه'],
        ['059', 'بانک سینا'],
        ['060', 'بانک قرض‌الحسنهٔ مهر ایران'],
        ['061', 'بانک شهر'],
        ['062', 'بانک آینده'],
        ['063', 'بانک انصار', true],
        ['064', 'بانک گردشگری'],
        ['065', 'بانک حکمت ایرانیان', true],
        ['066', 'بانک دی'],
        ['069', 'بانک ایران زمین'],
        ['070', 'بانک قرض‌الحسنهٔ رسالت'],
        ['073', 'مؤسسهٔ اعتباری کوثر', true],
        ['075', 'مؤسسهٔ اعتباری ملل'],
        ['078', 'بانک خاورمیانه'],
        ['079', 'بانک مهر اقتصاد', true],
        ['080', 'مؤسسهٔ اعتباری نور', true],
        ['095', 'بانک ایران و ونزوئلا'],
      ] as Array<[string, string, true?]>
    ).map(([code, nameFa, merged]) => [code, merged ? { code, nameFa, merged } : { code, nameFa }]),
  ),
);

/** Identify the bank of a Sheba, or `null` if the Sheba is malformed or the code is unknown. Does not check mod-97. */
export function bankFromSheba(input: string): BankInfo | null {
  const s = normalizeSheba(input);
  if (!SHEBA_RE.test(s)) return null;
  return IRAN_BANKS[s.slice(4, 7)] ?? null;
}

/**
 * Mask a Sheba for display: "IR06 **** **** **** **** 1234".
 * Only the country code, check digits and last 4 digits are shown.
 * Malformed input returns "****" so nothing is leaked.
 */
export function maskSheba(input: string): string {
  const s = normalizeSheba(input);
  if (!SHEBA_RE.test(s)) return '****';
  return `${s.slice(0, 4)} **** **** **** **** ${s.slice(-4)}`;
}
